<template>
  <div>
    <LazyBaseDropdown2
      ref="dropdownRef"
      id="language-switcher"
      class="language-switcher"
      :placement="isMainSite ? 'bottom-center' : 'bottom-end'"
      @close="() => (isPossibilitiesDropDownOpen = false)"
      @open="() => (isPossibilitiesDropDownOpen = true)"
    >
      <template #reference>
        <BaseButton2
          class="header-language"
          :class="{ active: isPossibilitiesDropDownOpen, mainSite: isMainSite }"
        >
          <BaseIcon2
            class="header-language-icon"
            :class="{ active: isPossibilitiesDropDownOpen && isMainSite }"
            id="icon-earth"
            height="24"
            width="24"
          />
          <BaseIcon2
            id="icon-cap"
            class="header-language-icon-caret"
            :class="{ active: isPossibilitiesDropDownOpen }"
            height="16"
            width="16"
          />
        </BaseButton2>
      </template>

      <template #content>
        <ul
          class="language-switcher__list"
          :class="{ mainSite: isMainSite }"
        >
          <li
            v-for="language in languageOptions"
            :key="language.value"
            class="language-switcher__item"
            @click="changeLanguage(language.value)"
          >
            <BaseIcon2
              :class="language.value === locale ? '' : 'color-white'"
              id="icon-check"
              color="var(--grey-2)"
              height="16"
              width="16"
            />
            <span class="language-switcher__item-name">{{ language.name }}</span>
          </li>
        </ul>
      </template>
    </LazyBaseDropdown2>
  </div>
</template>

<script lang="ts" setup>
interface Props {
  isMainSite?: boolean;
}

defineProps<Props>();

const isPossibilitiesDropDownOpen = ref(false);
const dropdownRef = ref(null);

const { locale, locales } = useI18n();

const languageOptions = computed(
  () =>
    locales.value?.map((language) => ({
      name: language.name,
      value: language.code,
    })) || [],
);

async function changeLanguage(langCode: string) {
  if (dropdownRef.value) {
    dropdownRef.value.closeDropdown();
  }

  if (langCode == locale.value) {
    return;
  }

  // Handle URL navigation with locale prefixes
  const router = useRouter();
  const route = useRoute();
  const currentPath = route.fullPath;
  const supportedLocales = languageOptions.value.map((lang) => lang.value);

  // Check if the current path starts with a locale prefix
  const hasLocalePrefix = supportedLocales.some(
    (locale) => currentPath.startsWith(`/${locale}/`) || currentPath === `/${locale}`,
  );

  if (hasLocalePrefix) {
    // Replace the current locale prefix with the new one
    const newPath = supportedLocales.reduce((path, locale) => {
      if (path.startsWith(`/${locale}/`)) {
        return `/${langCode}/${path.slice(locale.length + 2)}`;
      }
      if (path === `/${locale}`) {
        return `/${langCode}`;
      }
      return path;
    }, currentPath);

    // Navigate to the new path
    router.push(newPath);
  } else {
    // If no locale prefix, add the new one
    router.push(`/${langCode}${currentPath}`);
  }
}
</script>

<style scoped>
.header-language {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  padding: 12px 14px;
  column-gap: 10px;
  height: 60px;
  border: 0;
}

@media (--desktop) {
  .header-language {
    display: flex;
    transition-property: background-color;
    transition-duration: 0.2s;
    transition-timing-function: ease-in-out;
    padding: 12px 18px;
  }
}

.header-language.active {
  border: 0;
}

.header-language.active:not(.mainSite) {
  background-color: var(--grey-7);
}

.header-language:hover {
  border: 0;
}

.header-language-icon {
  color: var(--grey-3);
}

.header-language-icon.active {
  color: var(--orange-1);
}

.language-switcher {
  margin-left: 16px;
}

.language-switcher__list {
  list-style: none;
  padding: 0;
  margin: 0;
  min-width: 200px;
  border-radius: 8px;
  background-color: var(--white);
  box-shadow: 0 10px 16px 0 rgb(0 0 0 / 8%);
  z-index: calc(var(--z-index-header) + 1);
}

.mainSite.language-switcher__list {
  min-width: 120px;
}

.language-switcher__item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  column-gap: 14px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.language-switcher__item:hover {
  background-color: var(--grey-7);
}

.color-white {
  color: var(--white) !important;
}

.language-switcher__item-name {
  color: var(--grey-1);
  font-size: 12px;
}

.header-language-icon-caret {
  rotate: 90deg;
  color: var(--grey-3);
}

.header-language-icon-caret.active {
  transform: rotate(180deg);
}
</style>
